"use client";

import { useState, useRef, useEffect } from "react";
import type { WLanguageVersionsContent } from "@/lib/api/cms-window-content";
import { Flag } from "@/lib/i18n/Flag";
import { useT } from "@/lib/i18n/context";
import { wLanguageVersionsTranslations, type WLanguageVersionsTranslations } from "./WLanguageVersions.i18n";
import styles from "./WLanguageVersions.module.css";

/**
 * WLanguageVersions — renderer okna typu `w_language_versions` (przełącznik
 * języka). Dwa tryby UI wg `content.show_select`:
 *
 *   - **`false` (domyślny)** — pozioma lista (`ListMode`): wszystkie skonfigurowane
 *     języki widoczne, aktualny zaznaczony klasą `linkActive`. Każdy item to
 *     `<a href>` z pełnym URL z backendu (ILINK wygenerował z `cms_frontend_url`).
 *
 *   - **`true` (dropdown)** — `DropdownMode`: button z kodem aktualnego języka,
 *     po kliknięciu rozwija listę pozostałych języków. Click-outside zamyka
 *     dropdown (useEffect z document mousedown listener).
 *
 * **Server vs client:** komponent jest `"use client"` bo `DropdownMode` używa
 * useState/useRef. ListMode mógłby być server, ale trzymamy w jednym pliku
 * dla prostoty (cały W* registry zakłada jeden export).
 */
export function WLanguageVersions({ content }: WLanguageVersionsProps) {
  const t = useT(wLanguageVersionsTranslations);

  if (!content || content.items.length === 0) {
    return <nav className={styles.empty}>{t.empty}</nav>;
  }

  if (content.show_select) {
    return <DropdownMode items={content.items} t={t} />;
  }
  return <ListMode items={content.items} t={t} />;
}

export type WLanguageVersionsProps = {
  content: WLanguageVersionsContent;
};

/* ─── Pozioma lista flag (show_select=false) ─────────────────────────── */

function ListMode({ items, t }: { items: WLanguageVersionsContent["items"]; t: WLanguageVersionsTranslations }) {
  return (
    <nav className={styles.list} aria-label={t.ariaLabel}>
      {items.map((item) => (
        <a
          key={item.id}
          href={item.href}
          hrefLang={item.symbol}
          title={item.name}
          aria-current={item.current ? "true" : undefined}
          className={item.current ? `${styles.link} ${styles.linkActive}` : styles.link}
        >
          <Flag locale={item.symbol} className={styles.flag} height={14} />
          <span className={styles.name}>{item.name}</span>
        </a>
      ))}
    </nav>
  );
}

/* ─── Dropdown (show_select=true) ────────────────────────────────────── */

function DropdownMode({ items, t }: { items: WLanguageVersionsContent["items"]; t: WLanguageVersionsTranslations }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  /* Trigger = aktualny język. Lista (po expand) = WSZYSTKIE języki w kolejności
     z backendu (aktualny zaznaczony `linkActive`). Pierwotny Smarty template
     wykluczał current z listy — tu pokazujemy z `current` flag żeby user
     widział potwierdzenie wyboru. */
  const current = items.find((i) => i.current) ?? items[0];

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div className={styles.dropdown} ref={wrapperRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t.currentLanguageAria(current.name)}
      >
        <Flag locale={current.symbol} className={styles.triggerFlag} height={16} />
        <span className={styles.arrow} aria-hidden="true">
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
            <path
              d={open ? "M1 5L5 1L9 5" : "M1 1L5 5L9 1"}
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      {open && (
        <ul className={styles.menu} role="listbox">
          {items.map((item) => (
            <li key={item.id} role="option" aria-selected={item.current}>
              <a
                href={item.href}
                hrefLang={item.symbol}
                title={item.name}
                className={styles.menuLink}
              >
                <Flag locale={item.symbol} className={styles.flag} height={16} />
                <span className={styles.name}>{item.name}</span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
